import type { ReactNode } from "react";
import { CircleCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Bottom-centered confirmation "chip" toast: a dark rounded pill with a check
 * icon, a message, a divider and a trailing action.
 *
 * The surface uses `--color-action` / `--color-action-fg`, the only token pair
 * that is brand-black-on-light in light mode and inverts to near-white-on-dark
 * in dark mode — so the chip lifts off the page (and reads correctly) in BOTH
 * themes. Rendered via sonner's `toast.custom` with per-toast `unstyled` +
 * `position` so it does not affect the app's other (top-right) toasts.
 */

interface ChipToastOptions {
  message: ReactNode;
  /** Trailing action label. Defaults to "OK". */
  actionLabel?: string;
  /** Action handler; the toast is always dismissed afterwards. */
  onAction?: () => void;
  /** Auto-dismiss duration in ms. */
  duration?: number;
}

export function chipToast({
  message,
  actionLabel = "OK",
  onAction,
  duration,
}: ChipToastOptions) {
  return toast.custom(
    (id) => (
      <div className="flex w-fit max-w-104 items-center gap-3 rounded-xl bg-(--color-action) px-4 py-3 text-(--color-action-fg) shadow-lg">
        <CircleCheck className="size-5 shrink-0 text-(--color-action-fg)/70" />
        <span className="min-w-0 flex-1 text-sm leading-snug">{message}</span>
        <span className="h-5 w-px shrink-0 bg-(--color-action-fg)/20" />
        <button
          type="button"
          onClick={() => {
            onAction?.();
            toast.dismiss(id);
          }}
          className="shrink-0 text-sm font-semibold text-(--color-action-fg) transition-opacity hover:opacity-80"
        >
          {actionLabel}
        </button>
      </div>
    ),
    { unstyled: true, position: "bottom-center", duration },
  );
}
